import { type FC, useState } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { AlertTriangleIcon, CheckIcon } from '@/components/ui/icons'
import { CurationPhotosetSpaceRulesModal } from '@/features/photoset/components/curation-photoset-space-rules-modal'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type {
	PhotosetSpace,
	PhotosetSpacesChecks
} from '@/features/photoset/types'
import { Trans, useTranslation } from '@/translations/i18n'

interface CurationPhotosetSpaceCounterProps {
	spaceChecks: PhotosetSpacesChecks
	curationTouched: boolean
	spaceIndex: number
	spaceFlagValue: PhotosetSpace['flag_key_name']
	photosetSpaceId: PhotosetSpace['id']
}

const getCounterClasses = (isError: boolean, isSuccess: boolean) => {
	if (isError) {
		return 'bg-danger-100 border-danger-200 [&>p]:text-danger-600 [&>svg]:fill-danger-600'
	}
	if (isSuccess) {
		return 'bg-success-100 border-success-200 [&>p]:text-success-600 [&>svg]:fill-success-600'
	}
	return 'bg-neutral-100 border-neutral-200 [&>p]:text-neutral-600 [&>svg]:fill-neutral-600'
}

export const CurationPhotosetSpaceCounter: FC<
	CurationPhotosetSpaceCounterProps
> = ({
	spaceChecks,
	curationTouched,
	spaceIndex,
	spaceFlagValue,
	photosetSpaceId
}) => {
	const { t } = useTranslation()
	const photosetSpaceImageUnselectAll = usePhotosetStore(
		(state) => state.photosetSpaceImageUnselectAll
	)
	const [ruleModal, showRuleModal] = useState(false)

	const underMinimum =
		curationTouched &&
		spaceChecks?.selectedCounter < spaceChecks?.minimumSelection
	const needsFlags =
		curationTouched &&
		spaceChecks?.missingFlags &&
		spaceChecks.selectedCounter < spaceChecks.minimumSelection
	const isSuccess =
		curationTouched &&
		spaceChecks.selectedCounter >= spaceChecks.minimumSelection
	const isError =
		(spaceFlagValue === 'sufficient_coverage' && underMinimum) ||
		(!isSuccess && underMinimum && needsFlags)

	return (
		<div className='flex w-full items-center justify-between'>
			<div className='flex items-center'>
				<div
					className={`box-border flex items-center gap-2 rounded border px-2 py-1 [&>p]:text-sm ${getCounterClasses(isError, isSuccess)}`}
				>
					{isError && <AlertTriangleIcon size={18} />}
					{isSuccess && <CheckIcon size={18} />}
					<p>
						{t('photoset:curation.space.minimum_selected', {
							selectedCount: spaceChecks?.selectedCounter,
							minimumCount: spaceChecks?.minimumSelection
						})}
					</p>
				</div>

				{spaceChecks?.selectedCounter > 0 && (
					<ButtonLegacy
						className='min-h-[30px] px-4'
						onClick={() =>
							photosetSpaceImageUnselectAll(spaceIndex)
						}
						size='sm'
						type='button'
						variant='tertiary'
					>
						{t('common:unselect_all')}
					</ButtonLegacy>
				)}
			</div>

			<div className='flex items-center gap-2'>
				<p>
					<Trans
						components={{ bold: <b className='font-medium' /> }}
						i18nKey='photoset:curation.validation_rule'
						values={{
							rule:
								spaceFlagValue === 'sufficient_coverage'
									? t('photoset:curation.rules.coverage')
									: t('photoset:curation.rules.quality')
						}}
					/>
				</p>

				<ButtonLegacy
					className='p-0'
					onClick={() => showRuleModal(true)}
					size='sm'
					type='button'
					variant='tertiary'
				>
					{t('photoset:curation.learn_more')}
				</ButtonLegacy>
			</div>

			{ruleModal && (
				<CurationPhotosetSpaceRulesModal
					hideModal={() => showRuleModal(false)}
					photosetSpaceId={photosetSpaceId}
					spaceFlagValue={spaceFlagValue}
				/>
			)}
		</div>
	)
}

CurationPhotosetSpaceCounter.displayName = 'CurationPhotosetSpaceCounter'
